import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import FileInputField from "./shared/FileInputField";
import {
  fileNameFromPath,
  loadLogoPreviewWithAuthToSetter,
  parseApiError,
} from "./shared/onboardingUtils";

const emptyErrors = () => ({
  name: "",
  address: "",
  gst: "",
  mobile: "",
  phone: "",
  email: "",
  logourl: "",
  pinCode: "",
});

export default function ClinicOnboardingForm() {
  const API_BASE = process.env.REACT_APP_API_URL;

  const [formValues, setFormValues] = useState({
    name: "",
    clinicId: "",
    address: "",
    gst: "",
    mobile: "",
    phone: "",
    email: "",
    logourl: "",
    pinCode: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [errors, setErrors] = useState(emptyErrors());
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const loadLogoPreview = (fileName) =>
    loadLogoPreviewWithAuthToSetter(fileName, setLogoPreviewUrl, API_BASE);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const tenantId = localStorage.getItem("tenantId");
        const token = localStorage.getItem("auth_token");
        if (!tenantId || !token) {
          setChecking(false);
          return;
        }

        const response = await fetch(`${API_BASE}clinicDetails`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId,
            Accept: "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && (data.name || data.clinicId || data.address)) {
            setAlreadyExists(true);
            setExistingData(data);
            setFormValues({
              name: data.name || "",
              clinicId: data.clinicId || "",
              address: data.address || "",
              gst: data.gst || "",
              mobile: data.mobileNumber || "",
              phone: data.telephoneNumber || "",
              email: data.email || "",
              logourl: data.logourl || "",
              pinCode: data.pinCode || "",
            });
            if (data.logourl) loadLogoPreview(data.logourl);
          }
        } else if (response.status !== 404) {
          console.error("Error checking clinic details:", response.status);
        }
      } catch (error) {
        console.error("Error checking clinic details:", error);
      } finally {
        setChecking(false);
      }
    };

    checkExisting();
  }, [API_BASE]);

  const handleLogoFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setLogoFile(null);
      setLogoPreviewUrl("");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const validateField = (name, value) => {
    let error = "";
    const trimmed = String(value || "").trim();
    switch (name) {
      case "name":
        if (!trimmed) error = "Clinic name is required";
        break;
      case "address":
        if (!trimmed) error = "Address is required";
        break;
      case "pinCode":
        if (!trimmed) error = "Pincode is required";
        else if (!/^\d{6}$/.test(trimmed)) error = "Enter a valid 6-digit pincode";
        break;
      case "mobile":
        if (!trimmed) error = "Mobile is required";
        else if (!/^\d{10}$/.test(trimmed)) error = "Enter a valid 10-digit number";
        break;
      case "phone":
        if (trimmed && !/^\d{10}$/.test(trimmed)) error = "Enter a valid 10-digit number";
        break;
      case "email":
        if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) error = "Enter a valid email";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let filtered = value;
    if (name === "mobile" || name === "phone" || name === "pinCode") {
      filtered = value.replace(/\D/g, "");
    }
    setFormValues((prev) => ({ ...prev, [name]: filtered }));
    validateField(name, filtered);
  };

  const validateForm = () => {
    const fields = ["name", "address", "pinCode", "mobile", "phone", "email"];
    let valid = true;
    fields.forEach((f) => {
      if (!validateField(f, formValues[f])) valid = false;
    });
    return valid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const tenantId = localStorage.getItem("tenantId");
      const token = localStorage.getItem("auth_token");
      const isEditing = alreadyExists && isEditMode;

      if (isEditing) {
        const clinicPayload = {
          id: existingData?.id || "",
          name: formValues.name,
          address: formValues.address,
          gst: existingData?.gst || formValues.gst || "",
          telephoneNumber: formValues.phone || "",
          mobileNumber: formValues.mobile,
          email: formValues.email || "",
          clinicId: formValues.clinicId || tenantId,
          pinCode: formValues.pinCode,
          logourl: existingData?.logourl || "",
          categoryType: "SMARTCLINIC",
        };

        const res = await fetch(`${API_BASE}clinicDetails`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(clinicPayload),
        });

        if (!res.ok) throw new Error(await parseApiError(res));

        const responseData = await res.json();
        toast.success("Clinic details updated successfully");
        setExistingData(responseData);
        setIsEditMode(false);
        setLogoFile(null);
        if (responseData.logourl) loadLogoPreview(responseData.logourl);
        else setLogoPreviewUrl("");
      } else {
        const clinicPayload = {
          name: formValues.name,
          address: formValues.address,
          gst: formValues.gst || "",
          telephoneNumber: formValues.phone || "",
          mobileNumber: formValues.mobile,
          email: formValues.email || "",
          clinicId: tenantId,
          pinCode: formValues.pinCode,
          categoryType: "SMARTCLINIC",
          tenantId,
        };

        const formData = new FormData();
        formData.append(
          "clinic",
          new Blob([JSON.stringify(clinicPayload)], { type: "application/json" })
        );
        if (logoFile && logoFile instanceof File && logoFile.size > 0) {
          formData.append("file", logoFile, logoFile.name);
        }

        const res = await fetch(`${API_BASE}clinicDetails`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId,
            Accept: "application/json",
          },
          body: formData,
        });

        if (!res.ok) throw new Error(await parseApiError(res));

        const responseData = await res.json();
        localStorage.setItem("clinicId", responseData?.clinicId);
        localStorage.setItem("clinilogo", responseData?.logourl);
        localStorage.setItem("clinicname", responseData?.name);
        localStorage.setItem("organizationOnboarded", "true");
        toast.success("Clinic onboarded successfully");
        setAlreadyExists(true);
        setExistingData(responseData);
        setFormValues({
          name: responseData.name || "",
          clinicId: responseData.clinicId || "",
          address: responseData.address || "",
          gst: responseData.gst || "",
          mobile: responseData.mobileNumber || "",
          phone: responseData.telephoneNumber || "",
          email: responseData.email || "",
          logourl: responseData.logourl || "",
          pinCode: responseData.pinCode || "",
        });
        setLogoFile(null);
        if (responseData.logourl) loadLogoPreview(responseData.logourl);
        else setLogoPreviewUrl("");
        setErrors(emptyErrors());
      }
    } catch (err) {
      toast.error(err.message || "Failed to save clinic details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004276] mx-auto mb-4"></div>
          <p className="text-[#004276] text-lg">Checking clinic details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {alreadyExists && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="ml-0">
              <h3 className="text-sm font-medium text-yellow-800">Clinic already onboarded</h3>
              {!isEditMode && (
                <button
                  type="button"
                  className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-[#004276] rounded-md hover:opacity-90"
                  onClick={() => setIsEditMode(true)}
                >
                  Edit clinic details
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-6">
          <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-base font-semibold text-[#004276] mb-5">Clinic details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="name">Clinic name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter clinic name"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.name ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.name}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.name && <p className="mt-1.5 text-sm text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="gst">GST</label>
                <input
                  type="text"
                  id="gst"
                  name="gst"
                  placeholder="Enter GST number"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none border-gray-300 ${alreadyExists ? "opacity-60 cursor-not-allowed" : "focus:ring-2 focus:ring-[#3cba84] focus:border-[#3cba84]"}`}
                  value={formValues.gst}
                  onChange={alreadyExists ? undefined : handleChange}
                  disabled={alreadyExists}
                  readOnly={alreadyExists}
                />
              </div>
              <FileInputField
                id="logoFile"
                label="Clinic logo"
                className="md:col-span-2"
                file={logoFile}
                existingFileName={fileNameFromPath(existingData?.logourl)}
                previewUrl={logoPreviewUrl}
                disabled={alreadyExists}
                accept="image/*,.png,.jpg,.jpeg"
                onChange={handleLogoFileSelect}
                onDelete={() => {
                  setLogoFile(null);
                  setLogoPreviewUrl("");
                }}
              />
            </div>
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-base font-semibold text-[#004276] mb-5">Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="address">Address *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  placeholder="Enter full address"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.address ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.address}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.address && <p className="mt-1.5 text-sm text-red-600">{errors.address}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="pinCode">Pincode *</label>
                <input
                  type="tel"
                  id="pinCode"
                  name="pinCode"
                  placeholder="Enter 6-digit pincode"
                  maxLength="6"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.pinCode ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.pinCode}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.pinCode && <p className="mt-1.5 text-sm text-red-600">{errors.pinCode}</p>}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-base font-semibold text-[#004276] mb-5">Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="mobile">Mobile *</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  placeholder="Enter 10-digit mobile"
                  maxLength="10"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.mobile ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.mobile}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.mobile && <p className="mt-1.5 text-sm text-red-600">{errors.mobile}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Enter 10-digit phone (optional)"
                  maxLength="10"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.phone ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.phone}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.phone && <p className="mt-1.5 text-sm text-red-600">{errors.phone}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter email"
                  className={`w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
                    errors.email ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
                  }`}
                  value={formValues.email}
                  onChange={handleChange}
                  disabled={alreadyExists && !isEditMode}
                />
                {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          {alreadyExists ? (
            isEditMode ? (
              <>
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    const d = existingData || {};
                    setFormValues({
                      name: d.name || "",
                      clinicId: d.clinicId || "",
                      address: d.address || "",
                      gst: d.gst || "",
                      mobile: d.mobileNumber || "",
                      phone: d.telephoneNumber || "",
                      email: d.email || "",
                      logourl: d.logourl || "",
                      pinCode: d.pinCode || "",
                    });
                    setLogoFile(null);
                    if (d.logourl) loadLogoPreview(d.logourl);
                    else setLogoPreviewUrl("");
                    setErrors(emptyErrors());
                    setIsEditMode(false);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg ${
                    submitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
                  }`}
                  disabled={submitting}
                >
                  {submitting ? "Saving..." : "Save changes"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="px-5 py-2.5 bg-[#004276] text-white rounded-lg hover:opacity-90"
                onClick={() => setIsEditMode(true)}
              >
                Edit clinic
              </button>
            )
          ) : (
            <>
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setFormValues({
                    name: "",
                    clinicId: "",
                    address: "",
                    gst: "",
                    mobile: "",
                    phone: "",
                    email: "",
                    logourl: "",
                    pinCode: "",
                  });
                  setLogoFile(null);
                  setLogoPreviewUrl("");
                  setErrors(emptyErrors());
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg ${
                  submitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
                }`}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Onboard linic"}
              </button>
            </>
          )}
        </div>
      </form>
    </>
  );
}
